import { forwardRef, useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useReactToPrint } from 'react-to-print'
import { Button } from 'reactstrap'
import agency from '../../../api/agency/hooks'
import { Agency } from '../../../api/agency/types'
import { EvidenceDiary } from '../../../api/diaries/evidenceDiary/types'
import { IntermediaryDiary } from '../../../api/diaries/intermediaryDiary/types'
import styles from './PrintDiaryTable.module.css'
import { PrintTableProps } from './PrintDiaryTable.types'

const PrintDiaryTable = (props: PrintTableProps) => {
    const { header, data, title, className } = props

    const { t } = useTranslation()
    const [isTableVisible, setIsTableVisible] = useState(false)

    const { data: agencyData = {} as Agency } = agency.GetAgency.useQuery()

    const visibleColumns = header.filter((col) => col.id !== 'id' && col.id !== 'actions')
    const documnetDate = new Date()

    const handleAfterPrint = useCallback(() => {
        setIsTableVisible(false)
        return Promise.resolve()
    }, [])

    const handleBeforePrint = useCallback(() => {
        setIsTableVisible(true)
        return Promise.resolve()
    }, [])

    const TableContent = forwardRef<HTMLDivElement>((_, ref) => (
        <div ref={ref} className={`${styles.hiddenTable} ${isTableVisible ? styles.visible : ''}`}>
            <h3 className="text-center">{t('app.title')}</h3>
            <p>{documnetDate.toLocaleString()}</p>
            <div className="row">
                <div className="col">
                    <img src={agencyData.logo ?? undefined} width={150} className="my-4" />
                    <h3>{title}</h3>
                    <h5>{t('app.common.sheet')}</h5>
                </div>
                <div className="col">
                    <h5>
                        {agencyData.name}, {agencyData.street} {agencyData.streetNumber} {agencyData.zipCode} {agencyData.city} {agencyData.country};{' '}
                        {t('view.contact.general.phone')} {agencyData.phoneNumber}
                    </h5>
                    <p className="p-0 m-0">
                        {t('admin.agency.description.emailAgency')}: {agencyData.email}
                    </p>
                    <p className="p-0 m-0">
                        {t('app.common.web')}: {agencyData.websiteUrl}
                    </p>
                </div>
            </div>

            <table className={styles.table}>
                <thead>
                    <tr>
                        {visibleColumns?.map((col) => (
                            <th key={col.id} className={styles.headerCell}>
                                {col.header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {data?.map((row, i: number) => (
                        <tr key={i}>
                            {visibleColumns.map((col) => {
                                const id = col.id
                                let value

                                if (col.printValue) {
                                    value = col.printValue(row)
                                } else if (id in row) {
                                    value = row[id as keyof (IntermediaryDiary | EvidenceDiary)]
                                }

                                return (
                                    <td key={col.id} className={styles.tableCell}>
                                        {value as string}
                                    </td>
                                )
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    ))

    const contentRef = useRef<HTMLDivElement>(null)
    const handlePrint = useReactToPrint({
        contentRef,
        documentTitle: `${t('app.title')} - ${title}`,
        onAfterPrint: handleAfterPrint,
        onBeforePrint: handleBeforePrint,
    })

    return (
        <div className={className}>
            <Button color="link" onClick={() => handlePrint()} className={styles.printButton}>
                <i className="mdi mdi-printer-outline fs-24"></i>
            </Button>
            {isTableVisible && <TableContent ref={contentRef} />}
        </div>
    )
}

export default PrintDiaryTable
